import { environment } from '@accredible-frontend-v2/envs';
import { AccrediblePageData } from '@accredible-frontend-v2/models';
import { AccredibleApiService } from '@accredible-frontend-v2/services/api';
import { Injectable } from '@angular/core';
import { catchError, map, Observable } from 'rxjs';
import { Directory, DirectoryRegion, DirectorySortingOptions } from '../entities/directory.model';

const DIRECTORIES_BASE_PATH = '/spotlight/v1/directory_of_directories/spotlight_directories/search';

const API_URL = 'api.accredible.com';

// Requirement from Product for Directory of Directories (DoD).
// We need to support a single front-end app serving both US and EU regions,
// with users selecting the data source from the UI.
// Due to BE technical constraints, we cannot provide a unified gateway endpoint to merge data.
// This approach was agreed upon during discovery/technical sessions.
//
// To support this, we override the base API URL for EU. This ensures we can
// simulate different data on QA and, for consistency, also on lower
// environments.
const EU_ENV_OVERRIDES: Record<string, string> = {
  green: 'red',
  red: 'green',
  blue: 'green',
  qa: 'performance',
  production: 'eu',
};

export function getBaseApiUrl(region: DirectoryRegion): string {
  const env = environment.name;

  if (region === 'EU') {
    return `${EU_ENV_OVERRIDES[env] || env}.${API_URL}`;
  }

  return env === 'production' ? API_URL : `${env}.${API_URL}`;
}

interface DirectoryDto {
  id: number;
  name: string;
  domain: string;
  owner_name: string;
  owner_logo: string;
  description: string;
  directory_of_directories_members_count_enabled: boolean;
  members_count: number;
  skill_categories?: string[];
}

@Injectable({
  providedIn: 'root',
})
export class DirectoriesApiService extends AccredibleApiService {
  private readonly _sortingOptionsMap = new Map<DirectorySortingOptions, string>([
    [DirectorySortingOptions.MostDirectoryMembers, 'member_count_desc'],
    [DirectorySortingOptions.DirectoryNameAToZ, 'term_asc'],
    [DirectorySortingOptions.DirectoryNameZToA, 'term_desc'],
  ]);

  loadDirectories(
    directoryRegion: DirectoryRegion = 'US',
    sortingOption: DirectorySortingOptions = DirectorySortingOptions.MostDirectoryMembers,
    searchTerm: string = '',
    skill_categories: string[],
    page: number = 1,
  ): Observable<AccrediblePageData<Directory>> {
    const baseUrl = getBaseApiUrl(directoryRegion);
    const url = `https://${baseUrl}${DIRECTORIES_BASE_PATH}`;

    const sortBy =
      this._sortingOptionsMap.get(sortingOption) ??
      this._sortingOptionsMap.get(DirectorySortingOptions.MostDirectoryMembers)!;

    const params = {
      page,
      page_size: 10,
      term: searchTerm,
      sort_by: sortBy,
      skill_categories,
    };

    return this._post(url, params, true).pipe(
      map((res) => {
        const raw: AccrediblePageData<DirectoryDto> = this._handleResponse(res);
        const result = {
          meta: raw.meta,
          spotlight_directories: raw.spotlight_directories.map((dto) =>
            this.mapDtoToViewModel(dto, directoryRegion),
          ),
        };
        return result;
      }),
      catchError((res) => this._handleError(res)),
    );
  }

  private mapDtoToViewModel(dto: DirectoryDto, directoryRegion: DirectoryRegion = 'US'): Directory {
    const baseUrl = getBaseApiUrl(directoryRegion);
    const directoryUrl = `https://${baseUrl}/spotlight/v1/spotlight_directories/${dto.id}/spotlight_directory_analytics/track_directory_of_directories_directory_clicked`;

    return {
      id: String(dto.id),
      readOnly: false,
      logoUrl: dto.owner_logo,
      companyName: dto.owner_name,
      directoryName: dto.name,
      membersCount: dto.members_count,
      description: dto.description,
      categories: dto.skill_categories,
      directoryUrl,
    };
  }
}
